import React, { useState, useEffect } from 'react';

import { Link } from 'react-router-dom';
import { Button, Grid, Container, Typography } from '@mui/material';
import YouTubeIcon from '@mui/icons-material/YouTube';
import RestUtilities from '../services/RestUtilities';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  artistState,
  audioState,
  loadingState,
  toastState,
} from '../state/RecoilState';
import { ArtistMedia, Upload } from '../@types/api';
import MustPickArtist from '../components/Common/MustPickArtist';
import ModalFileManager from '../components/Common/ModalFileManager';
import { uploadType } from '../Constants';
import AudioCard from '../components/AudioCard';

const sortAudio = (a: any, b: any) => {
  if (a.isLive < b.isLive) {
    return 1;
  } else if (a.isLive > b.isLive) {
    return -1;
  } else if (a.orderIndex > b.orderIndex) {
    return 1;
  } else if (a.orderIndex < b.orderIndex) {
    return -1;
  }
  return 0;
};

const Audio = () => {
  const setToast = useSetRecoilState(toastState);
  const artist = useRecoilValue(artistState);
  const setLoading = useSetRecoilState(loadingState);
  const [audio, setAudio] = useRecoilState<ArtistMedia[] | undefined>(
    audioState
  );
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [show, setShow] = useState<boolean>(false);
  const fileType = uploadType.audio;

  useEffect(() => {
    const fetchMp3s = async (artistId: number) => {
      setLoading(true);
      const url = `api/ArtistMedia/Mp3s/${artistId}`;
      const response = await RestUtilities.get(url);
      console.log(response.content);
      if (response.ok) {
        setAudio(response.content);
        setToast('MP3s Loaded');
        setLoading(false);
      }
    };
    if (artist && !audio) {
      fetchMp3s(artist.artistId);
    }
  }, [artist, audio, setLoading, setAudio, setToast]);

  const onRemove = async (artistMedia: ArtistMedia) => {
    const url = `api/ArtistMedia/${artistMedia.artistMediaId}/${artist?.artistId}`;
    var response = await RestUtilities.delete(url);
    if (response.ok && audio) {
      setToast('MP3 Removed');
      const newAudio = audio.filter(
        (x) => x.artistMediaId !== artistMedia.artistMediaId
      );
      setAudio(newAudio);
    } else {
      setToast('Something went wrong');
    }
  };

  const onSaveAudio = async (updated: ArtistMedia) => {
    await RestUtilities.put(
      `api/ArtistMedia/${updated.artistMediaId}/${artist?.artistId}`,
      updated
    );
    if (audio) {
      const newAudio = audio.filter(
        (x) => x.artistMediaId !== updated.artistMediaId
      );
      newAudio.push(updated);
      setAudio(newAudio);
    }
  };

  const renderAudio = () => {
    if (!audio) return null;
    const sortedVideos = audio.toSorted(sortAudio);
    return sortedVideos.map((x: ArtistMedia) => {
      return (
        <Grid
          key={x.artistMediaId}
          item
          lg={3}
          md={4}
          sm={6}
          onClick={(e) => onItemSelect(e, x)}
          className='item-container'
        >
          <AudioCard
            audio={x}
            onSaveAudio={onSaveAudio}
            onRemoveAudio={onRemove}
            isSelected={x.artistMediaId === selectedId}
          />
        </Grid>
      );
    });
  };

  const onItemSelect = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    item: ArtistMedia
  ) => {
    if (selectedId !== item.artistMediaId) {
      e.preventDefault();
      setSelectedId(item.artistMediaId);
    }
  };

  const handleUploadSelect = async (item: Upload) => {
    if (!audio) {
      setShow(false);
      return;
    } else {
      if (!artist) return;
      const artistMedia: ArtistMedia = {
        uploadId: item.uploadId,
        mediaTypeId: 2,
        artistId: artist?.artistId,
        isLive: false,
        orderIndex: 99,
      };
      setLoading(true);
      const response = await RestUtilities.post(
        `api/ArtistMedia/${artist?.artistId}`,
        artistMedia
      );
      if (response.ok) {
        setAudio([...audio, response.content]);
      }
      setLoading(false);
      setShow(false);
    }
  };

  if (!artist) {
    return <MustPickArtist />;
  }

  return (
    <Container maxWidth='lg' className='YouTubeVideos'>
      <Typography variant='h4' gutterBottom>
        {artist.artistName} MP3 Audio
      </Typography>
      <Typography variant='body2'>
        This is the current mp3s appearing on your band's website. You can
        reorder and hide the MP3s You can edit file and title in the{' '}
        <Link to={'/files'}>File Editor</Link>.
      </Typography>

      {audio && (
        <>
          <Grid container spacing={2} className='audio-row'>
            <Grid item xs={12}>
              <Button
                variant='contained'
                color='primary'
                endIcon={<YouTubeIcon />}
                size='small'
                onClick={() => {
                  setShow(true);
                }}
                sx={{ my: 2 }}
              >
                Add new Mp3 Audio
              </Button>
            </Grid>
            {renderAudio()}
          </Grid>
        </>
      )}
      <ModalFileManager
        show={show}
        handleClose={() => setShow(false)}
        fileType={fileType}
        handleUploadSelect={handleUploadSelect}
      />
    </Container>
  );
};

export default Audio;
